import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../../api/client.js";
import {
  deleteFeed,
  patchFeed,
  restartFeed,
  skipFeed,
} from "../../api/feeds.js";
import { feedHealthStore } from "../../api/health-store.js";
import { nowPlayingStore } from "../../api/now-playing-store.js";
import type { Feed } from "../../api/types.js";

/**
 * The verbs one feed answers to, and what the API said when it was asked.
 *
 * Each of these is addressed to one feed and reaches one feed's engine instance:
 * skipping one disturbs no other, and the surface says so by never showing a result
 * anywhere but on whichever feed it belongs to.
 *
 * A control verb that fails is not swallowed and not dressed up. The API answers 503
 * when a feed's engine control endpoint is unreachable, and what the operator is told
 * is that sentence — not "something went wrong".
 */
export type FeedActionName =
  "skip" | "restart" | "enable" | "disable" | "delete";

export interface ActionOutcome {
  tone: "ok" | "warn" | "err";
  text: string;
}

const DONE: Record<Exclude<FeedActionName, "delete">, string> = {
  skip: "Skipped.",
  restart: "Restarted.",
  enable: "Enabled.",
  disable: "Disabled.",
};

/** A skipped feed changes track within a moment, not within a poll cadence. */
const SETTLE_MS = 2_000;

export function useFeedActions(
  feed: Pick<Feed, "id">,
  onChanged: () => void,
): {
  busy: FeedActionName | null;
  outcome: ActionOutcome | null;
  run: (action: FeedActionName) => void;
  dismiss: () => void;
} {
  const [busy, setBusy] = useState<FeedActionName | null>(null);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const changed = useRef(onChanged);
  changed.current = onChanged;

  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer);
    },
    [],
  );

  const settle = useCallback((feedId: string) => {
    nowPlayingStore.refresh(feedId);
    feedHealthStore.refresh(feedId);
    timers.current.push(
      setTimeout(() => {
        nowPlayingStore.refresh(feedId);
      }, SETTLE_MS),
    );
  }, []);

  const run = useCallback(
    (action: FeedActionName) => {
      if (busy) return;
      setBusy(action);
      setOutcome(null);
      perform(action, feed.id)
        .then(() => {
          if (action === "delete") {
            nowPlayingStore.forget(feed.id);
            feedHealthStore.forget(feed.id);
          } else {
            setOutcome({ tone: "ok", text: DONE[action] });
            settle(feed.id);
          }
          changed.current();
        })
        .catch((error: unknown) => {
          // The feed is gone from the brain and only its runtime namespace could not
          // be cleared. Saying "delete failed" would be false, and leaving the row on
          // screen would be worse.
          if (
            action === "delete" &&
            error instanceof ApiError &&
            error.code === "feed_namespace_pending"
          ) {
            setOutcome({ tone: "warn", text: error.detail || error.message });
            changed.current();
            return;
          }
          setOutcome({
            tone: "err",
            text:
              error instanceof Error
                ? error.message
                : "The API did not answer.",
          });
        })
        .finally(() => {
          setBusy(null);
        });
    },
    [busy, feed.id, settle],
  );

  const dismiss = useCallback(() => {
    setOutcome(null);
  }, []);

  return { busy, outcome, run, dismiss };
}

async function perform(action: FeedActionName, feedId: string): Promise<void> {
  switch (action) {
    case "skip":
      return skipFeed(feedId);
    case "restart":
      return restartFeed(feedId);
    case "enable":
      await patchFeed(feedId, { enabled: true });
      return;
    case "disable":
      await patchFeed(feedId, { enabled: false });
      return;
    case "delete":
      return deleteFeed(feedId);
  }
}
